import { ClubSearchSelect } from '~/pages/club/components/club-search-select';
import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { useAuthentication } from '~/hooks/useAuthentication';
import { schema, Schema } from './license-form.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '~/components/Button/Button';
import { AxiosResponse, isAxiosError } from 'axios';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { formatDate } from '~/utils/date';
import { LicenseWrite } from '~/apis/ssf';

interface Props {
  onSubmit: (data: Schema) => Promise<AxiosResponse<LicenseWrite, Error>>;
  mode: 'create' | 'edit';
  defaultValues?: Partial<Schema>;
  isLoading: boolean;
}

export function LicenseForm({ defaultValues, mode, isLoading, onSubmit }: Props) {
  const { isSuperUser } = useAuthentication();
  const navigate = useNavigate();

  const { formState, register, handleSubmit, setValue, setError, watch, trigger } = useForm<Schema>({
    defaultValues: defaultValues ?? {},
    resolver: zodResolver(schema),
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  async function trySubmit(payload: Schema) {
    if (!isSuperUser) {
      const now = new Date();

      const today = formatDate(now, 'yyyy-mm-dd');
      const firstDayNextYear = formatDate(new Date(now.getFullYear() + 1, 0, 1), 'yyyy-mm-dd');

      if (payload.start_date !== today && payload.start_date !== firstDayNextYear) {
        setError('start_date', {
          message: 'Du kan endast ange dagens datum, alternativt 1:a Januari nästkommande år',
        });
      }
    }

    try {
      const { data } = await onSubmit(payload);
      navigate(`/lifters/${data.person}/licenses`);
    } catch (error) {
      if (isAxiosError(error) && error.status === 400) {
        setError('start_date', {
          message: 'Personen har redan en licens för det angivna datumintervallet',
        });
      } else {
        throw error;
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(trySubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group formState={formState} htmlFor="club" label="Förening">
            <ClubSearchSelect
              placeholder="Sök på förening..."
              disabled={!isSuperUser}
              formState={formState}
              value={watch('club')}
              name="club"
              onChange={v => {
                setValue('club', v as [string, number], { shouldDirty: true });
                trigger('club');
              }}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group formState={formState} htmlFor="start_date" label="Startdatum">
            <Input.Field type="date" {...register('start_date')} formState={formState} id="start_date" required />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, lg: 6 }}>
          <Input.Group formState={formState} htmlFor="end_date" label="Slutdatum">
            <Input.Field
              {...register('end_date', { disabled: !isSuperUser })}
              formState={formState}
              id="end_date"
              type="date"
              required
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={isLoading}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
